import { FlareXMLDeclaration } from '@common/flare/constants/flare-xml-declaration.constant';
import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { defaultToFlareXML } from '@common/flare/util/prosemirror-flare-xml';
import { NodeIdAttrName, NodeIdMissingValue, NodeIdRootValue } from '@common/prosemirror/model/node-id';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { OperationAttrName } from '@common/prosemirror/operations/operations.constant';
import { getSchemaAttrs } from '@common/util/schema';
import { NodeSpec, ProseMirrorNode } from 'prosemirror-model';

/** Target XML node names. */
export enum TargetXmlNodeName {
  Target = 'CatapultTarget',
  Variables = 'Variables',
  Variable = 'Variable',
  DocumentProperties = 'DocumentProperties',
  PDF = 'PDF',
  PrintedOutput = 'PrintedOutput',
  Elasticsearch = 'Elasticsearch',
  Glossaries = 'Glossaries',
  RelationshipTables = 'RelationshipTables',
  MetaTagList = 'MetaTagList',
  MicroContentSearch = 'MicroContentSearch',
  FeaturedSnippets = 'FeaturedSnippets',
  KnowledgePanel = 'KnowledgePanel',
  microContentSubset = 'microContentSubset',
}

/** Target tag XML node attribute names. */
export enum TargetTagXmlAttributeName {
  Version = 'Version',
  Type = 'Type',
  // General
  Comment = 'Comment',
  MasterToc = 'MasterToc',
  ConditionTagExpression = 'ConditionTagExpression',
  ContentInclusionType = 'ContentInclusionType',
  ExcludeUnreferencedContent = 'ExcludeUnreferencedContent',
  StartupTopic = 'DefaultUrl',
  OutputFile = 'OutputFile',
  IncludeCshIdMetaTag = 'InjectCSHMetaTag',
  IncludeTocPathMetaTag = 'InjectTocPathMetaTag',
  OutputAnalyticsKey = 'OutputAnalyticsKey',
  LiveHelpType = 'LiveHelpType',
  OutputAnalyticsServer = 'OutputAnalyticsServer',
  // Appearance
  BrandingStylesheet = 'BrandingStylesheet',
  MasterStylesheet = 'MasterStylesheet',
  MasterPageLayout = 'MasterPageLayout',
  TemplatePage = 'MasterPage',
  Skin = 'Skin',
  ShowMadCapBackLink = 'ShowMadCapBacklink',
  ConvertStylesheetStylesToInline = 'ConvertStylesheetStylesToInlineStyles',
}

/** Target variable tag XML node attribute names. */
export enum TargetVariableTagXmlAttributeName {
  Name = 'Name',
}

/** Target document properties tag XML node attribute names. */
export enum DocumentPropertyTagXmlAttributeName {
  Title = 'Title',
  Author = 'Author',
  Subject = 'Subject',
  Keywords = 'Keywords',
  Category = 'Category',
  Comments = 'Comments',
  Company = 'Company',
  Manager = 'Manager',
  HyperlinkBase = 'HyperlinkBase',
  CopyrightStatus = 'CopyrightStatus',
  CopyrightNotice = 'CopyrightNotice',
  CopyrightInfoUrl = 'CopyrightInfoUrl',
}

/** Target PDF properties tag XML node attribute names. */
export enum PdfTagXmlAttributeName {
  Tagged = 'Tagged',
  Magnification = 'InitialMagnification',
  PageMode = 'PageMode',
  PageLayout = 'PageLayout',
  TitleBar = 'TitleBar',
  CollapsedBookmarks = 'CollapsedBookmarks',
  FullScreen = 'FullScreen',
}

export class TargetSchemaPlugin extends SchemaPlugin {
  nodes: Dictionary<NodeSpec> = {
    doc: {
      content: TargetXmlNodeName.Target,
      toFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions): string {
        let xml = FlareXMLDeclaration;

        for (let i = 0; i < node.childCount; i += 1) {
          const child = node.child(i);
          xml += (child.type.spec.toFlareXML ?? defaultToFlareXML)(child, options)
        }

        return xml;
      }
    },

    [TargetXmlNodeName.Target]: {
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdRootValue, skipExport: true },
        [OperationAttrName]: { default: undefined, skipExport: true },
        [TargetTagXmlAttributeName.Version]: { default: undefined },
        [TargetTagXmlAttributeName.Type]: { default: undefined },
        // General
        [TargetTagXmlAttributeName.Comment]: { default: undefined },
        [TargetTagXmlAttributeName.MasterToc]: { default: undefined },
        [TargetTagXmlAttributeName.ConditionTagExpression]: { default: undefined },
        [TargetTagXmlAttributeName.ContentInclusionType]: { default: undefined },
        [TargetTagXmlAttributeName.ExcludeUnreferencedContent]: { default: undefined },
        [TargetTagXmlAttributeName.StartupTopic]: { default: undefined },
        [TargetTagXmlAttributeName.OutputFile]: { default: undefined },
        [TargetTagXmlAttributeName.LiveHelpType]: { default: undefined },
        [TargetTagXmlAttributeName.OutputAnalyticsKey]: { default: undefined },
        [TargetTagXmlAttributeName.OutputAnalyticsServer]: { default: undefined },
        [TargetTagXmlAttributeName.IncludeCshIdMetaTag]: { default: undefined },
        [TargetTagXmlAttributeName.IncludeTocPathMetaTag]: { default: undefined },
        // Appearance
        [TargetTagXmlAttributeName.BrandingStylesheet]: { default: undefined },
        [TargetTagXmlAttributeName.MasterStylesheet]: { default: undefined },
        [TargetTagXmlAttributeName.TemplatePage]: { default: undefined },
        [TargetTagXmlAttributeName.MasterPageLayout]: { default: undefined },
        [TargetTagXmlAttributeName.Skin]: { default: undefined },
        [TargetTagXmlAttributeName.ShowMadCapBackLink]: { default: undefined },
        [TargetTagXmlAttributeName.ConvertStylesheetStylesToInline]: { default: undefined },
      },
      parseDOM: [{
        tag: TargetXmlNodeName.Target,
        getAttrs(dom: HTMLElement) {
          return getSchemaAttrs(dom, [TargetTagXmlAttributeName.Version, TargetTagXmlAttributeName.Type,
          // General
          TargetTagXmlAttributeName.Comment, TargetTagXmlAttributeName.MasterToc, TargetTagXmlAttributeName.ConditionTagExpression,
          TargetTagXmlAttributeName.ContentInclusionType, TargetTagXmlAttributeName.ExcludeUnreferencedContent,
          TargetTagXmlAttributeName.StartupTopic, TargetTagXmlAttributeName.OutputFile, TargetTagXmlAttributeName.LiveHelpType,
          TargetTagXmlAttributeName.OutputAnalyticsKey, TargetTagXmlAttributeName.OutputAnalyticsServer,
          TargetTagXmlAttributeName.IncludeCshIdMetaTag, TargetTagXmlAttributeName.IncludeTocPathMetaTag,
          // Appearance
          TargetTagXmlAttributeName.BrandingStylesheet, TargetTagXmlAttributeName.MasterStylesheet, TargetTagXmlAttributeName.TemplatePage,
          TargetTagXmlAttributeName.MasterPageLayout, TargetTagXmlAttributeName.Skin, TargetTagXmlAttributeName.ShowMadCapBackLink,
          TargetTagXmlAttributeName.ConvertStylesheetStylesToInline]);
        }
      }],
    },

    [TargetXmlNodeName.Variables]: {
      group: 'block',
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true }
      },
      parseDOM: [{
        tag: TargetXmlNodeName.Variables,
      }],
    },

    [TargetXmlNodeName.Variable]: {
      group: 'block allow_text_comment', // 'allow_text_comment' - allow comments inside value of the variable tag
      content: 'inline*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [TargetVariableTagXmlAttributeName.Name]: { default: undefined },
      },
      parseDOM: [{
        tag: TargetXmlNodeName.Variable,
        getAttrs(dom: HTMLElement) {
          return getSchemaAttrs(dom, [TargetVariableTagXmlAttributeName.Name]);
        }
      }],
    },

    [TargetXmlNodeName.DocumentProperties]: {
      group: 'block',
      isVoid: true,
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [DocumentPropertyTagXmlAttributeName.Title]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Author]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Subject]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Keywords]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Category]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Comments]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Company]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.Manager]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.HyperlinkBase]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.CopyrightStatus]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.CopyrightNotice]: { default: undefined },
        [DocumentPropertyTagXmlAttributeName.CopyrightInfoUrl]: { default: undefined },
      },
      parseDOM: [{
        tag: TargetXmlNodeName.DocumentProperties,
        getAttrs(dom: HTMLElement) {
          return getSchemaAttrs(dom, [DocumentPropertyTagXmlAttributeName.Title, DocumentPropertyTagXmlAttributeName.Author,
          DocumentPropertyTagXmlAttributeName.Subject, DocumentPropertyTagXmlAttributeName.Keywords,
          DocumentPropertyTagXmlAttributeName.Category, DocumentPropertyTagXmlAttributeName.Comments,
          DocumentPropertyTagXmlAttributeName.Company, DocumentPropertyTagXmlAttributeName.Manager,
          DocumentPropertyTagXmlAttributeName.HyperlinkBase, DocumentPropertyTagXmlAttributeName.CopyrightStatus,
          DocumentPropertyTagXmlAttributeName.CopyrightNotice, DocumentPropertyTagXmlAttributeName.CopyrightInfoUrl]);
        }
      }],
    },

    [TargetXmlNodeName.PDF]: {
      group: 'block',
      isVoid: true,
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [PdfTagXmlAttributeName.Tagged]: { default: undefined },
        [PdfTagXmlAttributeName.Magnification]: { default: undefined },
        [PdfTagXmlAttributeName.PageMode]: { default: undefined },
        [PdfTagXmlAttributeName.PageLayout]: { default: undefined },
        [PdfTagXmlAttributeName.TitleBar]: { default: undefined },
        [PdfTagXmlAttributeName.CollapsedBookmarks]: { default: undefined },
        [PdfTagXmlAttributeName.FullScreen]: { default: undefined },
      },
      parseDOM: [{
        tag: TargetXmlNodeName.PDF,
        getAttrs(dom: HTMLElement) {
          return getSchemaAttrs(dom, [PdfTagXmlAttributeName.Tagged, PdfTagXmlAttributeName.Magnification,
          PdfTagXmlAttributeName.PageMode, PdfTagXmlAttributeName.PageLayout,
          PdfTagXmlAttributeName.TitleBar, PdfTagXmlAttributeName.CollapsedBookmarks, PdfTagXmlAttributeName.FullScreen]);
        }
      }],
    },

    // target tags that are unsupported in central:
    // these tags are added to code-to-doc syncing might operate
    // with nested unsupported nodes of smaller size
    // instead of updating a one large node.
    [TargetXmlNodeName.PrintedOutput]: this.makeSimpleBlock(TargetXmlNodeName.PrintedOutput, true),
    [TargetXmlNodeName.Elasticsearch]: this.makeSimpleBlock(TargetXmlNodeName.Elasticsearch, true),
    [TargetXmlNodeName.Glossaries]: this.makeSimpleBlock(TargetXmlNodeName.Glossaries),
    [TargetXmlNodeName.RelationshipTables]: this.makeSimpleBlock(TargetXmlNodeName.RelationshipTables),
    [TargetXmlNodeName.MetaTagList]: this.makeSimpleBlock(TargetXmlNodeName.MetaTagList),
    [TargetXmlNodeName.MicroContentSearch]: this.makeSimpleBlock(TargetXmlNodeName.MicroContentSearch),
    [TargetXmlNodeName.FeaturedSnippets]: this.makeSimpleBlock(TargetXmlNodeName.FeaturedSnippets),
    [TargetXmlNodeName.KnowledgePanel]: this.makeSimpleBlock(TargetXmlNodeName.KnowledgePanel),
    [TargetXmlNodeName.microContentSubset]: this.makeSimpleBlock(TargetXmlNodeName.microContentSubset),
  };

  makeSimpleBlock(tagName: string, isVoid: boolean = false): NodeSpec {
    return {
      group: 'block',
      content: 'block*',
      isVoid: isVoid,
      parseDOM: [{ tag: tagName }]
    };
  }
}
